import type { BaseSyntheticEvent } from 'react';
import { useState } from 'react';
import {
  useForm,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useCreateCompanyUser } from '../hooks/useCreateCompanyUser';
import { createUserSchema, type CreateUserFormData } from '../types/company-admin.types';

interface CreateCompanyUserForm {
  open: boolean;
  setOpen: (open: boolean) => void;
  register: UseFormRegister<CreateUserFormData>;
  errors: FieldErrors<CreateUserFormData>;
  setValue: UseFormSetValue<CreateUserFormData>;
  watch: UseFormWatch<CreateUserFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
}

export function useCreateCompanyUserForm(): CreateCompanyUserForm {
  const [open, setOpen] = useState(false);
  const { createUser, isPending } = useCreateCompanyUser();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { initialRole: 'AGENT' },
  });
  const onSubmit = (data: CreateUserFormData): void => {
    createUser(
      { request: data },
      {
        onSuccess: () => {
          toast.success('User created successfully');
          reset();
          setOpen(false);
        },
        onError: () => toast.error('Failed to create user'),
      }
    );
  };

  return {
    open,
    setOpen,
    register,
    errors,
    setValue,
    watch,
    onSubmit: handleSubmit(onSubmit),
    isPending,
  };
}
